import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { getCreateAoFormObject } from './utils/create-ao.form-object';
import { AoRepository } from '@/store/ao-state/ao-state.repository';
import { SelectOption } from '@/components/form-builder/form-builder.types';
import { AoService } from '../ao.service';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, distinctUntilChanged, filter, forkJoin, map, Subscription, switchMap } from 'rxjs';
import { StatusComponent } from '@/components/shared/status/status.component';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { CommonModule } from '@angular/common';
import { AoWorkflowBlocksService } from '../ao-workflow-blocks.service';
import { Router } from '@angular/router';
import { AoDto, Ppm, PpmMilestone, WithMetadata } from '@/types';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';
import { PpmService } from '@/pages/ppm/ppm.service';
import { CurrencyService } from '@/pages/settings/currency/currency.service';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { aoCreateDraftValidationSchema } from '@/validations/ao/ao.validation';
import { FundingSourceService } from '@/pages/settings/funding-sources/funding-sources.service';

@Component({
  selector: 'sm-new-tender',
  imports: [PageHeaderComponent, FormBuilderComponent, StatusComponent, BlockBuilderComponent, CommonModule, SpinnerComponent],
  templateUrl: './new-tender.component.html',
  styleUrl: './new-tender.component.scss',
})
export class NewTenderComponent implements OnInit, OnDestroy {
  aoRepository = inject(AoRepository);
  aoService = inject(AoService);
  ppmService = inject(PpmService);
  currencyService = inject(CurrencyService);
  fundingSourceService = inject(FundingSourceService);
  aoWorkflowBlocksService = inject(AoWorkflowBlocksService);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  router = inject(Router);
  breadcrumb = [
    {
      label: 'ao.plural',
      routerLink: ['/call-for-tender'],
    },
    {
      label: 'ao.pages.newTender',
    },
  ];

  private ppmChangedSub = new Subscription();
  private milestoneChangedSub = new Subscription();
  currencyOptions?: SelectOption[];
  financialSourcesOptions?: (SelectOption & { examenPrealable: boolean })[];
  ppms: WithMetadata<Ppm>[] = [];
  ppmOptions: BehaviorSubject<SelectOption[]> = new BehaviorSubject<SelectOption[]>([]);
  milestones: WithMetadata<PpmMilestone>[] = [];
  milestoneOptions: BehaviorSubject<SelectOption[]> = new BehaviorSubject<SelectOption[]>([]);
  hasSlots = new BehaviorSubject<boolean>(false);
  hasFundingSources = new BehaviorSubject<boolean>(false);

  formObject = getFormBuilderObjectFactory();

  controlBlocks: DynamicBlocks[] = [];
  loading$ = new BehaviorSubject<boolean>(true);

  ngOnInit(): void {
    this.loadData();
    this.listenToPpmSelection();
    this.listenToMilestoneSelection();
  }

  ngOnDestroy() {
    this.ppmChangedSub.unsubscribe();
    this.milestoneChangedSub.unsubscribe();
    this.aoRepository.reset();
  }

  loadData() {
    this.loading$.next(true);

    this.controlBlocks = this.aoWorkflowBlocksService.getPreDraftAoBlocks({
      save: () => this.save(),
      rest: () => this.rest(),
    });

    forkJoin({
      availablePPMs: this.aoService.getAvailablePpms(),
      currencies: this.currencyService.getAll(),
      fundings: this.fundingSourceService.getAll(),
    }).subscribe({
      next: ({ availablePPMs, currencies, fundings }) => {
        this.ppms = availablePPMs;
        this.ppmOptions.next(
          availablePPMs.map((ppm: Ppm) => ({
            name: ppm.ref,
            code: `${ppm.id}-${ppm.version}`,
          }))
        );

        this.currencyOptions = currencies.map(curr => ({
          name: curr.description,
          code: curr.id,
        }));

        this.financialSourcesOptions = fundings.map(ref => ({
          name: ref.description,
          code: ref.id,
          examenPrealable: ref.bailleurDeFonds,
        }));

        this.formObject = getCreateAoFormObject({
          store: this.aoRepository,
          ppmOptions: this.ppmOptions,
          milestoneOptions: this.milestoneOptions,
          hasSlots: this.hasSlots,
          hasFundingSources: this.hasFundingSources,
          currencyOptions: this.currencyOptions,
          financialSourcesOptions: this.financialSourcesOptions,
        });

        this.loading$.next(false);
      },
      error: () => {
        this.loading$.next(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('ao.messages.error.loadingPpms'),
        });
      },
    });
  }

  listenToPpmSelection() {
    this.loading$.next(true);
    const ppmSelected$ = this.aoRepository.getObservable<SelectOption>('ppmSelected');

    if (ppmSelected$) {
      const sub = ppmSelected$
        .pipe(
          filter((ppm): ppm is SelectOption => !!ppm && !!ppm.code),
          switchMap(ppm => {
            const [ppmId, ppmVer] = ppm.code!.toString().split('-').map(Number);
            const selectedPpm = this.ppms.find(p => p.id === ppmId && p.version === ppmVer);
            this.aoRepository.setNested('response.ppm', selectedPpm);
            return this.aoService.getAvailableMilestones(ppmId, ppmVer);
          })
        )
        .subscribe({
          next: options => {
            this.milestones = options;
            this.milestoneOptions.next(
              options.map(m => ({
                name: m.realisationEnvisagees,
                code: `${m.id}-${m.version}`,
              }))
            );
            this.loading$.next(false);
          },
          error: () => {
            this.milestoneOptions.next([]);
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: this.translate.instant('ao.messages.error.loadingPpms'),
            });
          },
        });

      this.ppmChangedSub.add(sub);
    }
  }

  listenToMilestoneSelection() {
    this.loading$.next(true);
    const milestoneSelected$ = this.aoRepository.getObservable<SelectOption>('milestoneSelected');

    if (milestoneSelected$) {
      const sub = milestoneSelected$
        .pipe(
          filter((m): m is SelectOption => !!m && m.code !== '' && m.name !== ''),
          distinctUntilChanged((a, b) => a.code === b.code),
          map(selected => {
            const [milestoneId, milestoneVer] = selected.code!.toString().split('-').map(Number);
            const milestone = this.milestones.find(m => m.id === milestoneId && m.version === milestoneVer);
            this.aoRepository.setNested('response.realisation', milestone);
            return milestone;
          }),
          filter((milestone): milestone is WithMetadata<PpmMilestone> => !!milestone)
        )
        .subscribe({
          next: milestone => {
            this.hasSlots.next(!!milestone?.lots && milestone?.lots.length > 0);
            this.hasFundingSources.next(!!milestone?.financements && milestone?.financements.length > 0);
            this.aoRepository.setNested('createDto.nom', milestone.realisationEnvisagees);
            this.loading$.next(false);
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: this.translate.instant('ao.messages.error.loadingMilestones'),
            });
          },
        });

      this.milestoneChangedSub.add(sub);
    }
  }

  save() {
    const dto = this.aoRepository.get<AoDto>('createDto');
    const result = aoCreateDraftValidationSchema().safeParse(dto);
    if (result.success) {
      this.loading$.next(true);
      this.aoService.addAo(dto).subscribe({
        next: () => {
          this.rest();
          this.loading$.next(false);
          this.router.navigate(['call-for-tender']);
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('ao.messages.success.added'),
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('ao.messages.error.added'),
          });
        },
      });
    } else {
      this.aoRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }
  rest() {
    this.aoRepository.reset();
  }
}
